import React, { FunctionComponent, useState, useContext } from "react";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl-phraseapp";
import {
  Alert,
  Choice,
  Close,
  HelpDrawerToggle,
  HelpDrawer,
} from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../../helpers/intlHooks";
import { LowIncomeSubsidyOption } from "../../../@types";
import { NoIconAlert } from "../../../components";
import { sendTealiumEvent } from "../../../helpers";
import { TealiumConversions, linkToContacts } from "../../../helpers/CONSTANTS";
import { AppContext } from "../../../app/store";

interface HowDoYouGetHelpProps {
  answeredTypeOfHelp: (value: LowIncomeSubsidyOption) => void;
}

const HowDoYouGetHelp: FunctionComponent<HowDoYouGetHelpProps> = ({
  answeredTypeOfHelp,
}) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppContext);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const handleHelpSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const selectedHelpType = e.target.value as LowIncomeSubsidyOption;

    // istanbul ignore next: tested elsewhere
    sendTealiumEvent(
      "Find Plans - Choose Plans",
      TealiumConversions[selectedHelpType]
    );

    answeredTypeOfHelp(selectedHelpType);
  };

  return (
    <>
      <legend className="ds-h3 ds-u-margin--0 ds-u-margin-top--5 ds-u-margin-bottom--1 ds-u-padding--0">
        <h3>{t("landing_page.lis.do_you_get_help")}</h3>
      </legend>
      <Choice
        data-testid="medicaid-option"
        name="helpType"
        type="radio"
        value={LowIncomeSubsidyOption.Medicaid}
        onChange={handleHelpSelection}
        checkedChildren={NoIconAlert(
          "",
          t("landing_page.lis.medicaid_help_description")
        )}
      >
        {t("landing_page.lis.medicaid_help_label")}
      </Choice>

      <Choice
        data-testid="ssi-option"
        name="helpType"
        type="radio"
        value={LowIncomeSubsidyOption.SSI}
        onChange={handleHelpSelection}
        checkedChildren={NoIconAlert(
          "",
          t("landing_page.lis.ssi_help_description")
        )}
      >
        {t("landing_page.lis.ssi_help_label")}
      </Choice>

      <Choice
        name="helpType"
        type="radio"
        value={LowIncomeSubsidyOption.MSP}
        onChange={handleHelpSelection}
        checkedChildren={
          <Alert
            className="ds-u-margin-y--2 ds-u-margin-left--1"
            hideIcon={true}
          >
            {t("landing_page.lis.msp_help_description")}{" "}
            <HelpDrawerToggle
              helpDrawerOpen={showHelp}
              showDrawer={(): void => setShowHelp(true)}
              inline
            >
              {t("landing_page.lis.learn_more_about_these_programs")}
            </HelpDrawerToggle>
            {showHelp && (
              <div className="mct-c-help-drawer">
                <HelpDrawer
                  heading={t("landing_page.lis.msp_help_label")}
                  onCloseClick={(): void => setShowHelp(false)}
                  closeButtonText={<Close />}
                  ariaLabel={t("close_help_drawer")}
                >
                  <p>{t("landing_page.lis.msp_help_drawer_intro")}</p>
                  <ol>
                    <li>
                      <FormattedHTMLMessage id="landing_page.lis.msp_help_drawer_QMB" />
                    </li>
                    <li>
                      <FormattedHTMLMessage id="landing_page.lis.msp_help_drawer_SLMB" />
                    </li>
                    <li>
                      <FormattedHTMLMessage id="landing_page.lis.msp_help_drawer_QI" />
                    </li>
                    <li>
                      <FormattedHTMLMessage id="landing_page.lis.msp_help_drawer_QDWI" />
                    </li>
                  </ol>
                  <p>
                    {t("landing_page.lis.msp_help_drawer_check_if_qualify")}
                    <FormattedMessage
                      id="landing_page.lis.msp_help_drawer_more_info"
                      tagName="span"
                      values={{
                        link: (
                          <a
                            href={linkToContacts(language)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("landing_page.lis.msp_help_drawer_link_text")}
                          </a>
                        ),
                      }}
                    />
                  </p>
                </HelpDrawer>
              </div>
            )}
          </Alert>
        }
      >
        {t("landing_page.lis.msp_help_label")}
      </Choice>

      <Choice
        name="helpType"
        type="radio"
        value={LowIncomeSubsidyOption.SS}
        onChange={handleHelpSelection}
        checkedChildren={NoIconAlert(
          "",
          t("landing_page.lis.ss_help_description")
        )}
      >
        {t("landing_page.lis.ss_help_label")}
      </Choice>

      <Choice
        name="helpType"
        type="radio"
        value={LowIncomeSubsidyOption.Unsure}
        onChange={handleHelpSelection}
        checkedChildren={NoIconAlert(
          "",
          t("landing_page.lis.unsure_help_description")
        )}
      >
        {t("landing_page.lis.unsure_help_Label")}
      </Choice>

      <Choice
        name="helpType"
        type="radio"
        value={LowIncomeSubsidyOption.None}
        onChange={handleHelpSelection}
      >
        {t("landing_page.lis.no_help")}
      </Choice>
    </>
  );
};

export default HowDoYouGetHelp;
